<template>
    <div class="sip-theme-picker" ref="root">
        <button
            type="button"
            class="sip-icon-btn sip-header-btn--palette"
            :class="{ 'is-open': open }"
            title="Colour and fonts"
            aria-label="Colour and fonts"
            :aria-expanded="open ? 'true' : 'false'"
            @click.prevent="toggle"
        >
            <admin-icon name="palette" :size="18"></admin-icon>
        </button>
        <div v-show="open" class="sip-theme-panel" role="dialog" aria-label="Theme and fonts">
            <div class="sip-theme-tabs" role="tablist">
                <button
                    type="button"
                    role="tab"
                    class="sip-theme-tab"
                    :class="{ 'is-active': tab === 'colour' }"
                    :aria-selected="tab === 'colour' ? 'true' : 'false'"
                    @click="tab = 'colour'"
                >Colour</button>
                <button
                    type="button"
                    role="tab"
                    class="sip-theme-tab"
                    :class="{ 'is-active': tab === 'fonts' }"
                    :aria-selected="tab === 'fonts' ? 'true' : 'false'"
                    @click="openFonts"
                >Fonts</button>
            </div>

            <div v-show="tab === 'colour'" class="sip-theme-pane" role="tabpanel">
                <p class="sip-theme-hint">Light mode keeps a white sidebar and a neutral page. Theme colour is saved for the school and used on login, primary buttons, and the active menu item.</p>
                <div class="sip-theme-swatches" role="radiogroup" aria-label="Colour palettes">
                    <button
                        v-for="theme in themes"
                        :key="theme.id"
                        type="button"
                        class="sip-theme-swatch"
                        :class="{ 'is-active': themeId === theme.id }"
                        role="radio"
                        :aria-checked="themeId === theme.id ? 'true' : 'false'"
                        :title="theme.blurb"
                        @click="selectTheme(theme.id)"
                    >
                        <span class="sip-theme-swatch__dots" aria-hidden="true">
                            <i :style="{ background: theme.swatches[0] }"></i>
                            <i :style="{ background: theme.swatches[1] }"></i>
                            <i :style="{ background: theme.swatches[2] }"></i>
                        </span>
                        <span class="sip-theme-swatch__name">{{ theme.name }}</span>
                    </button>
                    <button
                        type="button"
                        class="sip-theme-swatch sip-theme-swatch--custom"
                        :class="{ 'is-active': themeId === 'custom' }"
                        role="radio"
                        :aria-checked="themeId === 'custom' ? 'true' : 'false'"
                        title="Pick your own accent colour"
                        @click="selectTheme('custom')"
                    >
                        <span class="sip-theme-swatch__dots" aria-hidden="true">
                            <i :style="{ background: custom.sidebar }"></i>
                            <i :style="{ background: custom.accent }"></i>
                            <i style="background: conic-gradient(#0f7a62, #206bc4, #8b2942, #c9a227, #0f7a62)"></i>
                        </span>
                        <span class="sip-theme-swatch__name">Custom</span>
                    </button>
                </div>
                <div v-if="themeId === 'custom'" class="sip-theme-custom">
                    <label class="sip-theme-color">
                        <span>Accent colour</span>
                        <span class="sip-theme-color__row">
                            <input type="color" :value="custom.accent" @input="onCustom('accent', $event.target.value)">
                            <input type="text" maxlength="7" :value="custom.accent" @change="onCustomHex('accent', $event.target.value)">
                        </span>
                    </label>
                    <label class="sip-theme-color">
                        <span>Sidebar (optional)</span>
                        <span class="sip-theme-color__row">
                            <input type="color" :value="custom.sidebar" @input="onCustom('sidebar', $event.target.value)">
                            <input type="text" maxlength="7" :value="custom.sidebar" @change="onCustomHex('sidebar', $event.target.value)">
                        </span>
                    </label>
                </div>
            </div>

            <div v-show="tab === 'fonts'" class="sip-theme-pane sip-theme-pane--fonts" role="tabpanel">
                <button
                    v-for="font in fonts"
                    :key="font.id"
                    type="button"
                    class="sip-font-card"
                    :class="{ 'is-active': fontId === font.id }"
                    :style="{ fontFamily: font.stack }"
                    @click="selectFont(font.id)"
                    @mouseenter="prefetchFont(font)"
                >
                    <span class="sip-font-card__name">{{ font.name }}</span>
                    <span class="sip-font-card__preview">{{ font.preview }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import {
    SCHOOL_THEMES,
    SCHOOL_FONTS,
    CUSTOM_THEME,
    applySchoolAppearance,
    readStoredPreference,
    setThemeId,
    setFontId,
    setCustomColors,
    ensureFontLink,
} from '../themes/school-themes'

export default {
    name: 'ThemePicker',
    data() {
        var prefs = readStoredPreference()
        return {
            open: false,
            tab: 'colour',
            themeId: prefs.themeId,
            fontId: prefs.fontId,
            custom: prefs.custom,
            themes: SCHOOL_THEMES,
            fonts: SCHOOL_FONTS,
            customMeta: CUSTOM_THEME,
        }
    },
    mounted() {
        applySchoolAppearance(readStoredPreference())
        var prefs = readStoredPreference()
        this.themeId = prefs.themeId
        this.fontId = prefs.fontId
        this.custom = prefs.custom
        this._onDoc = this.onDocClick.bind(this)
        this._onKey = this.onKey.bind(this)
        document.addEventListener('click', this._onDoc)
        document.addEventListener('keydown', this._onKey)
    },
    beforeDestroy() {
        document.removeEventListener('click', this._onDoc)
        document.removeEventListener('keydown', this._onKey)
    },
    methods: {
        toggle() {
            this.open = !this.open
        },
        openPanel() {
            this.open = true
        },
        openFonts() {
            this.tab = 'fonts'
            var fonts = this.fonts || []
            for (var i = 0; i < fonts.length; i++) {
                this.prefetchFont(fonts[i])
            }
        },
        onDocClick(e) {
            if (!this.open) return
            var root = this.$refs.root
            if (root && !root.contains(e.target)) this.open = false
        },
        onKey(e) {
            if (e.key === 'Escape') this.open = false
        },
        selectTheme(id) {
            this.themeId = id
            setThemeId(id)
        },
        selectFont(id) {
            this.fontId = id
            setFontId(id)
        },
        prefetchFont(font) {
            if (font && font.href) ensureFontLink(font.href)
        },
        onCustom(key, value) {
            var next = Object.assign({}, this.custom)
            next[key] = value
            this.custom = next
            this.themeId = 'custom'
            setCustomColors(next)
        },
        onCustomHex(key, value) {
            value = String(value || '').trim()
            if (!/^#[0-9A-Fa-f]{6}$/.test(value)) return
            this.onCustom(key, value)
        },
    },
}
</script>
